// apps/web/src/components/ui/SettingsList.tsx
// iOS-Settings-style building blocks: grouped inset lists whose rows lead with
// a tinted icon chip (colored rounded square + white glyph). One place owns the
// row layout so every settings screen looks identical.
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, type LucideIcon } from "lucide-react";
import { hapticTap } from "../../lib/haptics";

export function IconChip({ icon: Icon, tint }: { icon: LucideIcon; tint: string }) {
  return (
    <span
      aria-hidden="true"
      className="w-8 h-8 rounded-[9px] flex items-center justify-center shrink-0"
      style={{ background: tint }}
    >
      <Icon size={18} strokeWidth={2.2} className="text-white" />
    </span>
  );
}

export function SettingsGroup({ children }: { children: ReactNode }) {
  // Inset hairline dividers between rows come from .settings-group (index.css).
  return <div className="settings-group bg-surface rounded-2xl overflow-hidden">{children}</div>;
}

interface RowBase {
  icon: LucideIcon;
  tint: string;
  label: ReactNode;
  /** Small value text before the chevron (e.g. current language). */
  value?: ReactNode;
  /** Right-side content replacing value+chevron entirely (e.g. a toggle). */
  right?: ReactNode;
  danger?: boolean;
}

function RowInner({ icon, tint, label, value, right, danger }: RowBase) {
  return (
    <>
      <IconChip icon={icon} tint={danger ? "rgba(251,113,133,0.9)" : tint} />
      <span className={`flex-1 min-w-0 truncate text-sm ${danger ? "text-danger" : ""}`}>{label}</span>
      {right ?? (
        <>
          {value != null && <span className="text-sm text-fg-muted shrink-0">{value}</span>}
          <ChevronRight size={18} className="text-fg-faint shrink-0" aria-hidden="true" />
        </>
      )}
    </>
  );
}

const ROW_CLASS = "w-full flex items-center gap-3 px-4 py-3.5 text-left active:bg-surface-strong transition-colors";

export function SettingsLink({ to, ...row }: RowBase & { to: string }) {
  return (
    <Link to={to} className={ROW_CLASS}>
      <RowInner {...row} />
    </Link>
  );
}

export function SettingsButton({ onClick, disabled, ...row }: RowBase & { onClick: () => void; disabled?: boolean }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} className={`${ROW_CLASS} disabled:opacity-50`}>
      <RowInner {...row} />
    </button>
  );
}

export function SettingsAnchor({ href, ...row }: RowBase & { href: string }) {
  return (
    <a href={href} className={ROW_CLASS}>
      <RowInner {...row} />
    </a>
  );
}

/** Static row (no navigation) — e.g. an info line or a row hosting a switch. */
export function SettingsRow(row: RowBase & { children?: ReactNode }) {
  const { children, ...rest } = row;
  return (
    <div className="px-4 py-3.5">
      <div className="flex items-center gap-3">
        <RowInner {...rest} right={rest.right ?? <span />} />
      </div>
      {children}
    </div>
  );
}

/** The one switch style (iOS-like). */
export function Toggle({ checked, onChange, disabled, label }: {
  checked: boolean; onChange: (next: boolean) => void; disabled?: boolean; label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => { hapticTap(); onChange(!checked); }}
      className={`relative w-12 h-7 rounded-full transition-colors flex-shrink-0 disabled:opacity-50 ${
        checked ? "bg-brand" : "bg-surface-strong"
      }`}
    >
      <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white transition-transform ${checked ? "translate-x-5" : ""}`} />
    </button>
  );
}
